import { useEffect, useRef, useState } from 'react'
import { AlertCircle, Plus, X, Award, Upload, CheckCircle } from 'lucide-react'
import api from '../../services/api'

const EXPIRY_COLOUR: Record<string, string> = {
  expired: 'var(--red)',
  expiring_soon: 'var(--amber)',
  valid: 'var(--green)',
  no_expiry: 'var(--text-muted)',
}
const EXPIRY_BADGE: Record<string, string> = {
  expired: 'badge-critical',
  expiring_soon: 'badge-high',
  valid: 'badge-low',
  no_expiry: 'badge-medium',
}

const LICENCE_TYPES = [
  'White Card (WHS Induction)',
  'High Risk Work Licence',
  'Forklift Licence',
  'Crane Licence',
  'EWP Licence',
  'Rigging Licence',
  'Scaffolding Licence',
  'Confined Space Entry',
  'Working at Heights',
  'First Aid Certificate',
  'CPR Certificate',
  'Fire Warden',
  'Emergency Warden',
  'Asbestos Awareness',
  'Electrical Licence',
  'Gas Fitting Licence',
  'Driver Licence',
  'Dangerous Goods',
  'Other',
]

const emptyForm = {
  worker_id: '', competency_type: 'licence' as string, competency_name: '',
  licence_class: '', licence_number: '', issuing_authority: '', issue_date: '', expiry_date: '',
  ai_extracted: false, document_filepath: '',
}

export default function CompetenciesList() {
  const [data, setData] = useState<any[]>([])
  const [workers, setWorkers] = useState<any[]>([])
  const [contractors, setContractors] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState({ worker_id: '', expiry_status: '', company: '' })
  const [showAdd, setShowAdd] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [extracting, setExtracting] = useState(false)
  const [aiNotice, setAiNotice] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  const load = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (filter.worker_id) params.set('worker_id', filter.worker_id)
      if (filter.expiry_status) params.set('expiry_status', filter.expiry_status)
      if (filter.company) params.set('company', filter.company)
      const { data: res } = await api.get(`/competencies?${params}`)
      setData(res.data || [])
    } catch {}
    setLoading(false)
  }

  useEffect(() => {
    api.get('/workforce').catch(() => ({ data: { data: [] } })).then(r => setWorkers(r.data?.data || []))
    api.get('/contractors').catch(() => ({ data: [] })).then(r => setContractors(r.data?.data || r.data || []))
  }, [])

  useEffect(() => { load() }, [filter])

  const extractLicence = async (file: File) => {
    setExtracting(true)
    setAiNotice('')
    try {
      const fd = new FormData()
      fd.append('file', file)
      const { data: result } = await api.post('/competencies/upload-licence', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      const updates: Partial<typeof emptyForm> = { ai_extracted: true }
      if (result.competency_name) updates.competency_name = result.competency_name
      if (result.licence_class) updates.licence_class = result.licence_class
      if (result.licence_number) updates.licence_number = result.licence_number
      if (result.issuing_authority) updates.issuing_authority = result.issuing_authority
      if (result.issue_date) updates.issue_date = result.issue_date
      if (result.expiry_date) updates.expiry_date = result.expiry_date
      if (result.document_filepath) updates.document_filepath = result.document_filepath
      setForm(f => ({ ...f, ...updates }))
      setAiNotice(
        result.holder_name
          ? `Extracted from card for ${result.holder_name} — verify and select worker`
          : 'Fields auto-filled — please verify before saving'
      )
    } catch {
      setAiNotice('Extraction failed — please fill in fields manually')
    }
    setExtracting(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.worker_id || !form.competency_name) { setError('Worker and competency name are required'); return }
    setSaving(true)
    setError('')
    try {
      await api.post('/competencies', {
        worker_id: form.worker_id,
        competency_type: form.competency_type,
        competency_name: form.competency_name,
        licence_class: form.licence_class || undefined,
        licence_number: form.licence_number || undefined,
        issuing_authority: form.issuing_authority || undefined,
        issue_date: form.issue_date || undefined,
        expiry_date: form.expiry_date || undefined,
        ai_extracted: form.ai_extracted,
        document_filepath: form.document_filepath || undefined,
      })
      setShowAdd(false)
      setForm(emptyForm)
      setAiNotice('')
      load()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save')
    }
    setSaving(false)
  }

  const openAdd = () => { setForm(emptyForm); setAiNotice(''); setError(''); setShowAdd(true) }
  const closeAdd = () => { setShowAdd(false); setAiNotice('') }

  const expiryLabel = (status: string, expiry: string | null) => {
    if (status === 'expired') return `Expired ${expiry ? new Date(expiry).toLocaleDateString('en-AU') : ''}`
    if (status === 'expiring_soon') return `Expires ${expiry ? new Date(expiry).toLocaleDateString('en-AU') : ''}`
    if (status === 'valid') return expiry ? new Date(expiry).toLocaleDateString('en-AU') : '—'
    return 'No expiry'
  }

  const expiredCount = data.filter(d => d.expiry_status === 'expired').length
  const expiringSoonCount = data.filter(d => d.expiry_status === 'expiring_soon').length

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Competencies & Licences</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>{data.length} records</p>
        </div>
        <button onClick={openAdd} className="btn btn-primary flex items-center gap-2"><Plus size={16} /> Add Competency</button>
      </div>

      {expiredCount > 0 && (
        <div className="flex items-center gap-2 p-3 rounded-lg text-sm" style={{ background: 'var(--red-light)', color: 'var(--red)', border: '1px solid var(--red)' }}>
          <AlertCircle size={16} className="flex-shrink-0" />
          <strong>{expiredCount} expired</strong> licence{expiredCount > 1 ? 's' : ''} — workers must not perform licensed activities
        </div>
      )}
      {expiringSoonCount > 0 && (
        <div className="flex items-center gap-2 p-3 rounded-lg text-sm" style={{ background: 'var(--amber-light, #fffbeb)', color: 'var(--amber)', border: '1px solid var(--amber)' }}>
          <AlertCircle size={16} className="flex-shrink-0" />
          <strong>{expiringSoonCount} licence{expiringSoonCount > 1 ? 's' : ''}</strong> expiring within 90 days — arrange renewals
        </div>
      )}

      {/* Filters */}
      <div className="card p-4 flex flex-wrap gap-3">
        <select className="w-auto" value={filter.company} onChange={e => setFilter(f => ({ ...f, company: e.target.value }))}>
          <option value="">All companies</option>
          <option value="own">Own Staff</option>
          {contractors.map((c: any) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
        </select>
        <select className="w-auto" value={filter.worker_id} onChange={e => setFilter(f => ({ ...f, worker_id: e.target.value }))}>
          <option value="">All workers</option>
          {workers.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
        </select>
        <select className="w-auto" value={filter.expiry_status} onChange={e => setFilter(f => ({ ...f, expiry_status: e.target.value }))}>
          <option value="">All statuses</option>
          <option value="expired">Expired</option>
          <option value="expiring_soon">Expiring Soon</option>
          <option value="valid">Valid</option>
          <option value="no_expiry">No Expiry</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>Loading...</div>
      ) : data.length === 0 ? (
        <div className="card text-center py-16">
          <Award size={40} className="mx-auto mb-3" style={{ color: 'var(--text-muted)' }} />
          <p className="font-medium" style={{ color: 'var(--text)' }}>No competency records found</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Track licences, certifications and training records</p>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Worker</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Company</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Licence / Competency</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Number</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Issuing Authority</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Expiry</th>
                <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--text-secondary)' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td className="px-4 py-3 font-medium" style={{ color: 'var(--text)' }}>{c.worker_name}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{c.company_name || '—'}</td>
                  <td className="px-4 py-3" style={{ color: 'var(--text)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{c.competency_name}</span>
                      {c.onboarding_item_id && (
                        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--green-light)', color: 'var(--green)', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>onboarding</span>
                      )}
                      {c.ai_extracted === 1 && (
                        <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--purple-light)', color: 'var(--purple)', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>AI</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--text-secondary)' }}>{c.licence_number || '—'}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>{c.issuing_authority || '—'}</td>
                  <td className="px-4 py-3 text-xs font-medium" style={{ color: EXPIRY_COLOUR[c.expiry_status] }}>
                    {expiryLabel(c.expiry_status, c.expiry_date)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`badge ${EXPIRY_BADGE[c.expiry_status] || ''} capitalize`}>
                      {c.expiry_status?.replace('_', ' ')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="w-full max-w-lg rounded-2xl shadow-xl flex flex-col" style={{ background: 'var(--white)', maxHeight: 'calc(100vh - 2rem)' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
              <h2 className="font-semibold text-lg" style={{ color: 'var(--text)' }}>Add Competency / Licence</h2>
              <button onClick={closeAdd} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">

              {/* AI Upload */}
              <div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                  className="hidden"
                  onChange={e => {
                    const f = e.target.files?.[0]
                    if (f) extractLicence(f)
                    if (fileRef.current) fileRef.current.value = ''
                  }}
                />
                <button
                  type="button"
                  disabled={extracting}
                  onClick={() => fileRef.current?.click()}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-lg border-2 border-dashed text-sm font-medium transition-colors"
                  style={{
                    borderColor: form.ai_extracted ? 'var(--green)' : 'var(--border)',
                    color: extracting ? 'var(--text-muted)' : form.ai_extracted ? 'var(--green)' : 'var(--accent)',
                    background: form.ai_extracted ? 'var(--green-light)' : 'var(--bg)',
                    cursor: extracting ? 'not-allowed' : 'pointer',
                  }}
                >
                  {extracting ? (
                    <span>Scanning licence...</span>
                  ) : form.ai_extracted ? (
                    <><CheckCircle size={15} /><span>AI extracted — fields auto-filled below</span></>
                  ) : (
                    <><Upload size={15} /><span>Upload licence image or PDF to auto-fill fields</span></>
                  )}
                </button>
                {aiNotice && (
                  <p className="text-xs mt-1.5 font-medium" style={{ color: aiNotice.includes('failed') ? 'var(--red)' : 'var(--text-secondary)' }}>
                    {aiNotice}
                  </p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Worker *</label>
                <select value={form.worker_id} onChange={e => setForm(f => ({ ...f, worker_id: e.target.value }))} required>
                  <option value="">— Select worker —</option>
                  {workers.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Category</label>
                  <select value={form.competency_type} onChange={e => setForm(f => ({ ...f, competency_type: e.target.value }))}>
                    <option value="licence">Licence</option>
                    <option value="certificate">Certificate</option>
                    <option value="induction">Induction</option>
                    <option value="training">Training</option>
                    <option value="whs_training">WHS Training</option>
                    <option value="first_aid">First Aid</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Competency Name *</label>
                  <input value={form.competency_name} onChange={e => setForm(f => ({ ...f, competency_name: e.target.value }))} placeholder="e.g. High Risk Work Licence" list="comp-list" required />
                  <datalist id="comp-list">
                    {LICENCE_TYPES.map(t => <option key={t} value={t} />)}
                  </datalist>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Licence Number</label>
                  <input value={form.licence_number} onChange={e => setForm(f => ({ ...f, licence_number: e.target.value }))} placeholder="e.g. WHS-12345" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Class / Category</label>
                  <input value={form.licence_class} onChange={e => setForm(f => ({ ...f, licence_class: e.target.value }))} placeholder="e.g. LF, C6" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Issuing Authority</label>
                  <input value={form.issuing_authority} onChange={e => setForm(f => ({ ...f, issuing_authority: e.target.value }))} placeholder="e.g. SafeWork NSW" />
                </div>
                <div>{/* spacer */}</div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Issue Date</label>
                  <input type="date" value={form.issue_date} onChange={e => setForm(f => ({ ...f, issue_date: e.target.value }))} />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Expiry Date</label>
                  <input type="date" value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} />
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
                  <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{error}
                </div>
              )}

            </div>
            <div className="flex gap-3 px-6 py-4 border-t flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Record'}</button>
              <button type="button" className="btn btn-secondary" onClick={closeAdd}>Cancel</button>
            </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
